import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import QuickRecordPage from './pages/QuickRecordPage'
import { AccessibilityProvider } from './lib/accessibility'
import { AppLayout } from './components/shared/AppLayout'
import LoginPage from './pages/LoginPage'
import SetPasswordPage from './pages/SetPasswordPage'
import JoinPage from './pages/JoinPage'
import UnsubscribePage from './pages/UnsubscribePage'
import GetStartedPage from './pages/GetStartedPage'
import SignupPage from './pages/SignupPage'
import PendingApprovalPage from './pages/PendingApprovalPage'
import { TermsPage, PrivacyPage } from './pages/LegalPages'
import { DashboardPage } from './pages/DashboardPage'
import { FarmMapPage } from './pages/FarmMapPage'
import { VarietiesPage } from './pages/VarietiesPage'
import { PlantsPage } from './pages/PlantsPage'
import { HarvestPage } from './pages/HarvestPage'
import { RipeningPage } from './pages/RipeningPage'
import { SpoilagePage } from './pages/SpoilagePage'
import ContainersPage from './pages/ContainersPage'
import CustomersPage from './pages/CustomersPage'
import MarketingPage from './pages/MarketingPage'
import DroneMapsPage from './pages/DroneMapsPage'
import PreordersPage from './pages/PreordersPage'
import { SupportPage } from './pages/SupportPage'
import { CalendarPage } from './pages/CalendarPage'
import { CustomFormsPage } from './pages/CustomFormsPage'
import { TeamPage } from './pages/TeamPage'
import { CohortsPage } from './pages/CohortsPage'
import { PropagationPage } from './pages/PropagationPage'
import { IrrigationPage } from './pages/IrrigationPage'
import { ActivityPage } from './pages/ActivityPage'
import { WeatherPage } from './pages/WeatherPage'
import { InventoryPage } from './pages/InventoryPage'
import { CommercePage } from './pages/CommercePage'
import VendorsPage from './pages/VendorsPage'
import IntegrationsPage from './pages/IntegrationsPage'
import { EquipmentPage } from './pages/EquipmentPage'
import { LaborPage } from './pages/LaborPage'
import { BlocksPage } from './pages/BlocksPage'
import { ValuationPage } from './pages/ValuationPage'
import { AnimalsPage } from './pages/AnimalsPage'
import { NurseryPage } from './pages/NurseryPage'
import { LabTestsPage } from './pages/LabTestsPage'
import { SensorsPage } from './pages/SensorsPage'
import { WaterPage } from './pages/WaterPage'
import { PermitsPage } from './pages/PermitsPage'
import { SeedsPage } from './pages/SeedsPage'
import { SoilPage } from './pages/SoilPage'
import { SettingsPage } from './pages/SettingsPage'
import { AgExemptionPage } from './pages/AgExemptionPage'
import { AuditLogsPage } from './pages/AuditLogsPage'
import AccountingPage from './pages/AccountingPage'
import ExpensesPage from './pages/ExpensesPage'
import { YieldImpactPage } from './pages/YieldImpactPage'
import ProjectsPage from './pages/ProjectsPage'
import { ReportsPage } from './pages/ReportsPage'
import DocsPage from './pages/DocsPage'
import AdminPortal from './pages/admin/AdminPortal'
import { OnboardingWizard } from './components/shared/OnboardingWizard'
import { ReleaseModal } from './components/shared/ReleaseModal'
import { useAuthStore } from './lib/store'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 60_000, retry: 1 },
  },
})

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { token, status } = useAuthStore()
  const loc = useLocation()
  if (!token) {
    // Preserve the intended destination so a field-QR scan while logged out
    // (e.g. /r/plant/123) returns there after login instead of the dashboard.
    const dest = loc.pathname + loc.search
    const q = dest && dest !== '/' ? `?next=${encodeURIComponent(dest)}` : ''
    return <Navigate to={`/login${q}`} replace />
  }
  // Logged in but awaiting admin approval — keep them on the pending page
  if (status === 'pending') return <Navigate to="/pending" replace />
  return <>{children}</>
}

function RequireSuperuser({ children }: { children: React.ReactNode }) {
  const { token, role } = useAuthStore()
  if (!token) return <Navigate to="/login" replace />
  if (role !== 'superuser') return <Navigate to="/" replace />
  return <>{children}</>
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AccessibilityProvider>
      <BrowserRouter>
        {/* Global overlays — render outside the route tree so they survive navigation */}
        <ReleaseModal />
        <OnboardingWizard />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          {/* Public — no auth required */}
          {/* Password reset + invite acceptance: both set a password via
              /auth/reset-confirm. Must stay OUTSIDE RequireAuth so the
              email link doesn't bounce to /login before the form loads. */}
          <Route path="/reset-password" element={<SetPasswordPage mode="reset" />} />
          <Route path="/accept-invite" element={<SetPasswordPage mode="invite" />} />
          <Route path="/get-started" element={<GetStartedPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/join/:code" element={<JoinPage />} />
          <Route path="/pending" element={<PendingApprovalPage />} />
          {/* Legal pages — public, app-served. Signup checkboxes + footer link here
              (grafty.io apex isn't live yet, §133). Content mirrors grafty-sites. */}
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          {/* One-click marketing unsubscribe (§166) — public, reached from an email
              link by a logged-out recipient. Must stay OUTSIDE RequireAuth. */}
          <Route path="/unsubscribe/:tid/:token" element={<UnsubscribePage />} />
          {/* Field QR deep-links — login-gated, full-screen (no app shell). A worker
              scans a plant/block sticker → records picking/activity. See QuickRecordPage. */}
          <Route path="/r/plant/:id" element={<RequireAuth><QuickRecordPage kind="plant" /></RequireAuth>} />
          <Route path="/r/block/:id" element={<RequireAuth><QuickRecordPage kind="block" /></RequireAuth>} />
          <Route
            path="/"
            element={
              <RequireAuth>
                <AppLayout />
              </RequireAuth>
            }
          >
            {/* FARM */}
            <Route index element={<DashboardPage />} />
            <Route path="activity" element={<ActivityPage />} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="map" element={<FarmMapPage />} />

            {/* GROWING */}
            <Route path="plants" element={<PlantsPage />} />
            <Route path="blocks" element={<BlocksPage />} />
            <Route path="cohorts" element={<CohortsPage />} />
            <Route path="valuation" element={<ValuationPage />} />
            <Route path="varieties" element={<VarietiesPage />} />
            <Route path="harvest" element={<HarvestPage />} />
            <Route path="ripening" element={<RipeningPage />} />
            <Route path="spoilage" element={<SpoilagePage />} />
            <Route path="containers" element={<ContainersPage />} />
            <Route path="customers" element={<CustomersPage />} />
            <Route path="marketing" element={<MarketingPage />} />
            <Route path="drone-maps" element={<DroneMapsPage />} />
            <Route path="preorders" element={<PreordersPage />} />
            <Route path="support" element={<SupportPage />} />
            <Route path="yield-impact" element={<YieldImpactPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="propagation" element={<PropagationPage />} />
            <Route path="nursery" element={<NurseryPage />} />
            <Route path="seeds" element={<SeedsPage />} />

            {/* OPERATIONS */}
            <Route path="equipment" element={<EquipmentPage />} />
            <Route path="labor" element={<LaborPage />} />
            <Route path="team" element={<TeamPage />} />
            <Route path="animals" element={<AnimalsPage />} />
            <Route path="forms" element={<CustomFormsPage />} />
            <Route path="irrigation" element={<IrrigationPage />} />
            <Route path="weather" element={<WeatherPage />} />

            {/* SCIENCE */}
            <Route path="lab-tests" element={<LabTestsPage />} />
            <Route path="sensors" element={<SensorsPage />} />

            {/* RESOURCES */}
            <Route path="inventory" element={<InventoryPage />} />
            <Route path="soil" element={<SoilPage />} />
            <Route path="water" element={<WaterPage />} />

            {/* COMMERCE */}
            <Route path="commerce" element={<CommercePage />} />
            <Route path="vendors" element={<VendorsPage />} />

            {/* ACCOUNTING */}
            <Route path="accounting" element={<AccountingPage />} />
            <Route path="expenses" element={<ExpensesPage />} />

            {/* PROJECTS */}
            <Route path="projects" element={<ProjectsPage />} />

            {/* COMPLIANCE */}
            <Route path="permits" element={<PermitsPage />} />
            <Route path="ag-exemption" element={<AgExemptionPage />} />
            <Route path="integrations" element={<IntegrationsPage />} />
            <Route path="audit" element={<AuditLogsPage />} />

            {/* SETTINGS */}
            <Route path="settings" element={<SettingsPage />} />
            <Route path="docs" element={<DocsPage />} />
          </Route>

          {/* Superuser admin portal — separate layout, no tenant sidebar */}
          <Route
            path="/admin"
            element={
              <RequireSuperuser>
                <AdminPortal />
              </RequireSuperuser>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </AccessibilityProvider>
    </QueryClientProvider>
  )
}
